import React, { useEffect, useMemo, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import MuiSelect from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';

export interface SelectOption {
  label?: React.ReactNode;
  value: string | number | boolean;
  disabled?: boolean;
  [key: string]: any;
}

export interface SelectProps {
  value?: any;
  defaultValue?: any;
  onChange?: (value: any, option?: any) => void;
  options?: SelectOption[];
  children?: React.ReactNode;
  allowClear?: boolean;
  showSearch?: boolean;
  mode?: 'multiple' | 'tags';
  disabled?: boolean;
  style?: React.CSSProperties;
  className?: string;
  placeholder?: string;
  filterOption?: boolean | ((input: string, option: any) => boolean);
  optionFilterProp?: string;
  size?: 'small' | 'middle' | 'large';
  dropdownMatchSelectWidth?: boolean;
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
  optionLabelProp?: string;
  notFoundContent?: React.ReactNode;
  onClick?: React.MouseEventHandler;
  loading?: boolean;
  deleteIcon?: React.ReactElement;
  /** Mở list ngay khi ô được focus (ô Excel: 1 click là hiện list). */
  openOnFocus?: boolean;
  [key: string]: any;
}

function childrenToOptions(children: React.ReactNode): SelectOption[] {
  const opts: SelectOption[] = [];
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === Option) {
      const p = child.props as OptionProps;
      opts.push({ label: p.children, value: p.value, disabled: p.disabled });
    }
  });
  return opts;
}

export interface OptionProps {
  value: string | number | boolean;
  children?: React.ReactNode;
  disabled?: boolean;
  label?: React.ReactNode;
  [key: string]: any;
}

export const Option: React.FC<OptionProps> = () => null;

function labelOf(opt: SelectOption): string {
  if (typeof opt.label === 'string') return opt.label;
  if (opt.label != null) return String(opt.label);
  return String(opt.value);
}

/** Autocomplete + đóng list khi click ngoài (capture — không bị stopPropagation của panel cha). */
const SearchableSelect: React.FC<{
  multiple: boolean;
  muiSize: 'small' | 'medium';
  disabled?: boolean;
  className?: string;
  options: SelectOption[];
  selected: any;
  allowClear?: boolean;
  labelOf: (opt: SelectOption) => string;
  filterFn: (input: string, opt: SelectOption) => boolean;
  onChange?: (value: any, option?: any) => void;
  ListboxProps?: any;
  resolvedSlotProps: Record<string, any>;
  placeholder?: string;
  onClick?: React.MouseEventHandler;
  kiotSelectFieldSx: object;
  style?: React.CSSProperties;
  deleteIcon?: React.ReactElement;
  openOnFocus?: boolean;
}> = ({
  multiple,
  muiSize,
  disabled,
  className,
  options,
  selected,
  allowClear,
  labelOf: getLabel,
  filterFn,
  onChange,
  ListboxProps,
  resolvedSlotProps,
  placeholder,
  onClick,
  kiotSelectFieldSx,
  style,
  deleteIcon,
  openOnFocus,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const AutocompleteAny = Autocomplete as React.ComponentType<any>;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target) return;
      if (rootRef.current?.contains(target)) return;
      if (target.closest?.('.MuiAutocomplete-popper, .MuiModal-root')) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [open]);

  return (
    <Box ref={rootRef} sx={{ width: '100%', minWidth: 0 }}>
      <AutocompleteAny
        multiple={multiple}
        size={muiSize}
        disabled={disabled}
        className={className}
        options={options}
        value={(multiple ? selected : selected) as any}
        disableClearable={!allowClear}
        open={open}
        openOnFocus={openOnFocus}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        getOptionLabel={(opt) => getLabel(opt)}
        isOptionEqualToValue={(a, b) => a.value === b.value}
        filterOptions={(opts, state) => opts.filter((o) => filterFn(state.inputValue, o))}
        onChange={(_, newVal) => {
          if (multiple) {
            onChange?.((newVal as SelectOption[]).map((o) => o.value));
          } else {
            onChange?.((newVal as SelectOption | null)?.value ?? undefined, newVal);
          }
        }}
        ListboxProps={ListboxProps}
        slotProps={Object.keys(resolvedSlotProps).length ? resolvedSlotProps : undefined}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder={placeholder}
            onClick={onClick}
            sx={{ ...kiotSelectFieldSx, ...style }}
          />
        )}
        renderTags={(tagValue: SelectOption[], getTagProps: any) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={String(option.value)}
              label={getLabel(option)}
              size="small"
              deleteIcon={deleteIcon}
            />
          ))
        }
      />
    </Box>
  );
};

export const Select: React.FC<SelectProps> & { Option: typeof Option } = ({
  value,
  onChange,
  options: optionsProp,
  children,
  allowClear,
  showSearch,
  mode,
  disabled,
  style,
  className,
  placeholder,
  filterOption = true,
  optionFilterProp = 'label',
  size,
  onClick,
  ListboxProps,
  slotProps,
  PaperProps,
  deleteIcon,
  openOnFocus,
}) => {
  const options = useMemo(() => {
    const fromChildren = childrenToOptions(children);
    return optionsProp?.length ? optionsProp : fromChildren;
  }, [optionsProp, children]);

  const [inner, setInner] = useState<any>(undefined);
  const val = value !== undefined ? value : inner;

  const multiple = mode === 'multiple' || mode === 'tags';
  const muiSize = size === 'large' ? 'medium' : size === 'middle' ? 'small' : 'small';

  const kiotSelectFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      fontSize: '0.8125rem',
      minHeight: 32,
    },
    '& .MuiInputBase-input': {
      fontSize: '0.8125rem',
      py: 0.75,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#d0d0d0',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#b0b0b0',
    },
  };

  const filterFn = (input: string, opt: SelectOption) => {
    if (filterOption === false) return true;
    if (typeof filterOption === 'function') return filterOption(input, opt);
    const hay =
      optionFilterProp === 'value'
        ? String(opt.value)
        : labelOf(opt);
    return hay.toLowerCase().includes(input.toLowerCase());
  };

  if (showSearch || multiple) {
    const selected = multiple
      ? options.filter((o) => (Array.isArray(value) ? value : []).some((v) => v === o.value || String(v) === String(o.value)))
      : options.find((o) => o.value === value || String(o.value) === String(value)) ?? null;

    const resolvedSlotProps = {
      ...(slotProps || {}),
      ...(PaperProps ? { paper: { ...(slotProps as any)?.paper, ...PaperProps } } : {}),
    };

    return (
      <SearchableSelect
        multiple={multiple}
        muiSize={muiSize}
        disabled={disabled}
        className={className}
        options={options}
        selected={selected}
        allowClear={allowClear}
        labelOf={labelOf}
        filterFn={filterFn}
        onChange={onChange}
        ListboxProps={ListboxProps}
        resolvedSlotProps={resolvedSlotProps}
        placeholder={placeholder}
        onClick={onClick}
        kiotSelectFieldSx={kiotSelectFieldSx}
        style={style}
        deleteIcon={deleteIcon}
        openOnFocus={openOnFocus}
      />
    );
  }

  const matched = options.find((o) => o.value === val || String(o.value) === String(val))
  const safeValue = val == null || val === '' || !matched ? '' : matched.value

  return (
    <FormControl
      fullWidth
      size="small"
      className={className}
      sx={{ ...kiotSelectFieldSx, ...style }}
      onClick={onClick}
    >
      <MuiSelect
        displayEmpty
        disabled={disabled}
        variant="outlined"
        value={safeValue}
        onChange={(e) => {
          const v = e.target.value;
          if (value === undefined) setInner(v);
          const next = v === '' ? undefined : v;
          const opt = options.find((o) => o.value === next || String(o.value) === String(next));
          onChange?.(next, opt);
        }}
        renderValue={(selected) => {
          if (selected === '' || selected == null) return <span style={{ opacity: 0.5 }}>{placeholder}</span>;
          const opt = options.find((o) => o.value === selected);
          return opt ? opt.label : selected;
        }}
        sx={{
          borderRadius: '8px',
          fontSize: '0.8125rem',
          '& .MuiSelect-select': { py: 0.75 },
        }}
      >
        {(allowClear || safeValue === '') && (
          <MenuItem value="" sx={allowClear ? undefined : { display: 'none' }}>
            <em>{placeholder || '—'}</em>
          </MenuItem>
        )}
        {options.map((opt) => (
          <MenuItem key={String(opt.value)} value={opt.value as any} disabled={opt.disabled}>
            {opt.label}
          </MenuItem>
        ))}
      </MuiSelect>
    </FormControl>
  );
};

Select.Option = Option;

export default Select;
